type MarketingIconKind =
  | 'calendar'
  | 'clock'
  | 'link'
  | 'users'
  | 'embed'
  | 'bell'
  | 'building'
  | 'briefcase'
  | 'phone'
  | 'sparkle'
  | 'shield'
  | 'wallet'

interface MarketingBenefit {
  key: string
  title: string
  body: string
}

interface MarketingFeature {
  key: string
  iconKind: MarketingIconKind
  title: string
  body: string
}

interface MarketingFeatureCategory {
  key: string
  iconKind: MarketingIconKind
  title: string
  body: string
  bullets: string[]
}

interface MarketingUseCase {
  key: string
  iconKind: MarketingIconKind
  title: string
  body: string
  examples: string[]
}

const MARKETING_BENEFITS: MarketingBenefit[] = [
  {
    key: 'fast-setup',
    title: 'Set up in minutes',
    body: 'Connect a calendar, set your hours, share your link. Live booking page the same day — no implementation call required.',
  },
  {
    key: 'no-seat-tax',
    title: 'No seat tax',
    body: 'Add the whole team without your subscription doing weird math. Pricing scales with usage, not with how many hands touched the calendar.',
  },
  {
    key: 'built-in',
    title: 'Built into the product, not bolted on',
    body: 'QuickCal sits next to your CRM, forms, and inbox. One login, one billing line, bookings showing up where your team already lives.',
  },
]

const MARKETING_FEATURES: MarketingFeature[] = [
  {
    key: 'multi-event-type',
    iconKind: 'calendar',
    title: 'One link, many event types',
    body: 'Discovery call, demo, deep-dive, follow-up. One booking link, four flavours — guests pick the right one.',
  },
  {
    key: 'availability',
    iconKind: 'clock',
    title: 'Real availability rules',
    body: 'Working hours, buffers, minimum notice, date overrides. Set it once, stop double-booking yourself forever.',
  },
  {
    key: 'sync',
    iconKind: 'link',
    title: 'Two-way calendar sync',
    body: 'Google and Microsoft. Block off what is already on your calendar so guests can never pick a slot you cannot actually take.',
  },
  {
    key: 'team',
    iconKind: 'users',
    title: 'Round-robin and team scheduling',
    body: 'Spread bookings evenly across the team or weight them by territory. No more "I think it is my turn" Slack threads.',
  },
  {
    key: 'embed',
    iconKind: 'embed',
    title: 'Embed anywhere',
    body: 'Drop the booking widget onto your site, your landing page, or an email signature. Same flow, no rebuild.',
  },
  {
    key: 'reminders',
    iconKind: 'bell',
    title: 'Smart reminders & follow-ups',
    body: 'Email and SMS reminders before the meeting, automatic follow-ups after. Show-up rate goes up, your to-do list goes down.',
  },
]

const MARKETING_FEATURE_CATEGORIES: MarketingFeatureCategory[] = [
  {
    key: 'booking-pages',
    iconKind: 'calendar',
    title: 'Booking pages',
    body: 'Public booking links that look like a real product, not a hosted form. Brand them, configure them per event type, and embed them anywhere.',
    bullets: [
      'Multiple durations per event type — let guests pick 15, 30, or 60',
      'Per-event-type images and rich-text descriptions with links',
      'Custom forms — multi-step flows or single-page intake',
      'Prefill from email links, CRM exports, or referring chat threads',
      'Your branding, your colours, your fonts — or stripped-back QuickCal default',
    ],
  },
  {
    key: 'availability',
    iconKind: 'clock',
    title: 'Availability rules',
    body: 'Every rule a real scheduling engine needs. Configure once, trust it not to double-book you on a Friday afternoon.',
    bullets: [
      'Multi-day working hours per availability profile',
      'Date-specific overrides for holidays, conferences, and personal time',
      'Buffer time before and after each meeting',
      'Minimum notice to prevent surprise same-day bookings',
      'Max bookings per day or per week to cap your meeting load',
    ],
  },
  {
    key: 'sync',
    iconKind: 'link',
    title: 'Calendar sync',
    body: 'Two-way sync with the calendars your team actually uses. Conflicts get caught the moment they happen.',
    bullets: [
      'Google Calendar with two-way write access',
      'Microsoft Graph for Outlook and Microsoft 365',
      'Conflict detection across every connected calendar at once',
      'Automatic Google Meet and Microsoft Teams link generation',
      'Real-time block-off — guests see your latest availability, not yesterday’s',
    ],
  },
  {
    key: 'team',
    iconKind: 'users',
    title: 'Team scheduling',
    body: 'Distribute bookings across the team without spreadsheets, Slack debates, or a half-broken Zapier flow.',
    bullets: [
      'Round-robin assignment for even load',
      'Collective scheduling when everyone needs to be free',
      'Weighted distribution by territory, segment, or capacity',
      'Per-team availability profiles that respect individual hours',
      'Reassignment when someone is out — bookings stay live, not stuck',
    ],
  },
  {
    key: 'embed',
    iconKind: 'embed',
    title: 'Embeddable widgets',
    body: 'The booking flow as an embed, not a redirect away from your site.',
    bullets: [
      'Inline embed, pop-up trigger, or full-screen overlay',
      'Configurable size and theme tokens to match your site',
      'No QuickCal branding on paid plans',
      'Same booking flow as the public page — no separate codebase',
      'Pre-fill from URL parameters or upstream chat context',
    ],
  },
  {
    key: 'reminders',
    iconKind: 'bell',
    title: 'Reminders & follow-ups',
    body: 'Reminders the night before. Follow-ups the day after. Show-up rates that look like a different product.',
    bullets: [
      'Custom email and SMS reminder templates per event type',
      'Configurable reminder timing — 24 hours, 1 hour, or anything you want',
      'Automatic follow-up sequences after the meeting wraps',
      'No-show detection and re-engagement triggers',
      'Show-up rate analytics broken down per event type',
    ],
  },
]

const MARKETING_USE_CASES: MarketingUseCase[] = [
  {
    key: 'real-estate',
    iconKind: 'building',
    title: 'Real estate',
    body: 'Built for the original use case — property tours, showings, and open houses that actually happen on the calendar they were booked on.',
    examples: [
      'In-person property tours with location and parking notes',
      'Group showings with attendee caps',
      'Open-house slots with rolling availability',
    ],
  },
  {
    key: 'sales',
    iconKind: 'briefcase',
    title: 'Sales',
    body: 'Cut the back-and-forth out of every discovery call. Sales engineers, AEs, and SDRs all on the same booking surface.',
    examples: [
      'Inbound discovery calls assigned by round-robin',
      'Demo bookings with SE shadowing on collective availability',
      'Follow-up bookings prefilled from a closed-won workflow',
    ],
  },
  {
    key: 'customer-success',
    iconKind: 'phone',
    title: 'Customer success',
    body: 'Onboarding, QBRs, and check-ins on a calendar your customers can actually read — and your CSMs do not have to babysit.',
    examples: [
      'Onboarding kickoffs with custom intake forms',
      'Quarterly business reviews on recurring slots',
      'Health-check calls triggered from your CRM',
    ],
  },
  {
    key: 'recruiting',
    iconKind: 'sparkle',
    title: 'Recruiting',
    body: 'Phone screens, panel interviews, take-home reviews. All routed to the right interviewer without a single calendar.app exchange.',
    examples: [
      'Phone screens on the recruiter’s availability',
      'Panel interviews on collective availability across the hiring team',
      'Take-home review slots with the hiring manager only',
    ],
  },
  {
    key: 'consulting',
    iconKind: 'wallet',
    title: 'Consultants & freelancers',
    body: 'Charge for your time without booking it twice. Buffers, minimum notice, and embeddable widgets that match your site.',
    examples: [
      'Paid 30-minute consults with prefilled intake',
      'Project kickoffs after a Stripe checkout',
      'Free 15-minute introductions to qualify new leads',
    ],
  },
  {
    key: 'support',
    iconKind: 'shield',
    title: 'Customer support',
    body: 'Escalations and triage calls when tickets stop working. Move the conversation to a real time slot without leaving the helpdesk.',
    examples: [
      'Tier-2 escalation calls with the right specialist',
      'Triage slots with the on-call engineer',
      'White-glove support sessions for enterprise accounts',
    ],
  },
]

export {
  MARKETING_BENEFITS,
  MARKETING_FEATURES,
  MARKETING_FEATURE_CATEGORIES,
  MARKETING_USE_CASES,
}
export type {
  MarketingIconKind,
  MarketingBenefit,
  MarketingFeature,
  MarketingFeatureCategory,
  MarketingUseCase,
}
